import React, { useCallback, use, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { PointClickEventObject } from 'highcharts';
import { appendScreenFiltersQuery } from './chart-utils';
import {
    hasEvaluationsFromDifferentModel,
    mapRisksToBubbleSeries,
    getModelXValues,
    getModelMaxImpact,
} from './risk-map.utils';
import { buildExactMatchTrackedChange } from '@components-clean/core/filter-ui/action-bar/shared/filter.service';
import {
    CategoryType,
    FieldDefinitionFieldTypeNumeric,
    FilterCategoryItems,
} from '@components-clean/core/filter-ui/action-bar/shared/interfaces';
import PanelFrame from '@components-clean/core/panel-frame';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { RiskDashboardContext } from '@pages-clean/risk-assessment/risk-dashboard/context';
import { PanelType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { AllPaths } from '@utils/router/all-paths';

const createYAxisLabelFormatter = (maxImpact: number) =>
    function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
        const value = this.value as number;
        return value < 0 || value > maxImpact ? '' : value.toString();
    };

const IMPACT_CATEGORY: FilterCategoryItems = {
    id: 0,
    index: 0,
    fieldKey: 'impact',
    label: '',
    isDefault: false,
    dataType: FieldDefinitionFieldTypeNumeric.Int,
};

const LTD_CATEGORY: FilterCategoryItems = {
    id: 0,
    index: 0,
    fieldKey: 'ltd',
    label: '',
    isDefault: false,
    dataType: FieldDefinitionFieldTypeNumeric.Int,
};

const LIKELIHOOD_CATEGORY: FilterCategoryItems = {
    id: 0,
    index: 0,
    fieldKey: 'likelihood',
    label: '',
    isDefault: false,
    dataType: FieldDefinitionFieldTypeNumeric.Int,
};

const RiskMapChart = () => {
    const { t } = useTranslation('ram');
    const {
        risks: allRisks,
        filteredRisks: risks,
        includeDetectability,
        evaluationModelId,
        evaluationModel,
        filterableRisks,
        categories,
        screenTrackedChanges,
        includeObsolete,
        filterVersion,
    } = use(RiskDashboardContext);
    const navigate = useNavigate();

    const hasUnevaluated = useMemo(() => risks.some((r) => !r.evaluationHistory?.length), [risks]);

    const xValues = useMemo(() => {
        if (!evaluationModel) return undefined;
        const modelValues = getModelXValues(evaluationModel);
        if (!hasUnevaluated) return modelValues;
        return Array.from(new Set([0, ...modelValues])).sort((a, b) => a - b);
    }, [evaluationModel, hasUnevaluated]);

    const xCategories = useMemo(() => xValues?.map(String), [xValues]);

    const maxImpact = useMemo(() => (evaluationModel ? getModelMaxImpact(evaluationModel) : 0), [evaluationModel]);

    const valueToIndex = useMemo(() => {
        if (!xValues) return undefined;
        return Object.fromEntries(xValues.map((v, i) => [v, i]));
    }, [xValues]);

    const series = useMemo(() => {
        const raw = mapRisksToBubbleSeries(risks, t, includeDetectability);
        if (!valueToIndex) return raw;

        return raw.map((s) => ({
            ...s,
            data: s.data.map((point) => ({
                ...point,
                x: valueToIndex[point.x] ?? point.x,
            })),
        }));
    }, [risks, t, includeDetectability, valueToIndex]);

    const handleClick = useCallback(
        (event: PointClickEventObject) => {
            NavigationService.setViewRiskDetails();

            const actualX = xValues?.[event.point.x] ?? event.point.x;
            const { rag } = event.point as unknown as { rag?: string };
            const isUnevaluated = actualX === 0 && event.point.y === 0 && rag === 'NONE';
            const impactValue = isUnevaluated ? null : event.point.y;

            const ratingParent = categories.find((c) => c.items.some((item) => item.fieldKey === 'riskRating'));
            const ratingItem = ratingParent?.items.find((item) => item.fieldKey === 'riskRating');
            const ratingValue = rag ? t(`riskLog.riskRating.${rag}`) : undefined;

            const lxdCategory = includeDetectability ? LTD_CATEGORY : LIKELIHOOD_CATEGORY;

            const clickTrackedChanges = [
                buildExactMatchTrackedChange(filterableRisks, IMPACT_CATEGORY, impactValue, CategoryType.Risk),
                buildExactMatchTrackedChange(filterableRisks, lxdCategory, actualX, CategoryType.Risk),
                ratingItem && buildExactMatchTrackedChange(filterableRisks, ratingItem, ratingValue, ratingParent!.id),
            ].filter((change) => change !== undefined);

            navigate(
                appendScreenFiltersQuery(
                    AllPaths.path('ra-risk-log') ?? '',
                    clickTrackedChanges,
                    screenTrackedChanges,
                    includeObsolete,
                ),
            );
        },
        [
            xValues,
            filterableRisks,
            categories,
            screenTrackedChanges,
            includeObsolete,
            includeDetectability,
            navigate,
            t,
        ],
    );

    const xAxisTitle = includeDetectability
        ? t('riskDashboard.riskMap.xAxisTitle1')
        : t('riskDashboard.riskMap.xAxisTitle2');

    const hasStaleEvaluations = useMemo(() => {
        if (!evaluationModel) return false;
        return hasEvaluationsFromDifferentModel(allRisks, evaluationModel);
    }, [allRisks, evaluationModel]);

    const hasEvaluationModel = !!evaluationModelId && evaluationModelId !== Constants.ObjectId.Empty;

    const customOptions = useMemo(
        () => ({
            chart: {
                zooming: {
                    type: 'xy' as const,
                },
            },
            plotOptions: {
                series: {
                    cursor: 'pointer',
                    point: {
                        events: {
                            click: handleClick,
                        },
                    },
                },
            },
            xAxis: {
                ...(xCategories && { categories: xCategories }),
                ...(hasUnevaluated ? { minPadding: 0.05 } : {}),
            },
            yAxis: {
                ...(hasUnevaluated ? {} : { min: 0 }),
                ...(maxImpact > 0 ? { max: maxImpact + 0.5, tickInterval: 1 } : {}),
                labels: {
                    formatter: createYAxisLabelFormatter(maxImpact),
                },
            },
        }),
        [xCategories, hasUnevaluated, handleClick, maxImpact],
    );

    let customEmptyPanelMessage: string | undefined;

    if (hasStaleEvaluations) {
        customEmptyPanelMessage = t('riskDashboard.riskMap.staleEvaluationsWarning');
    }

    if (!hasEvaluationModel) {
        customEmptyPanelMessage = t('riskDashboard.noEvalModel');
    }

    return (
        <PanelFrame
            title={t('riskDashboard.riskMap.title')}
            panelType={PanelType.GRAPH}
            graphType="BUBBLE"
            panelData={series}
            hidePanelSetup
            xAxisTitle={xAxisTitle}
            yAxisTitle={t('riskDashboard.riskMap.yAxisTitle')}
            customOptions={customOptions}
            data-testid="risk-map-chart"
            version={filterVersion}
            customEmptyPanelMessage={customEmptyPanelMessage}
            customHeight={380}
        />
    );
};

export default RiskMapChart;
